/**
 * AttendanceMonthlyTable — Story 106-4 (E-106 HR UX Max)
 *
 * Változások 106-4 scope-ban:
 *  - Cella-tap → részletek Sheet drawer (scheduledStart, overtime, break, note, source)
 *  - Havi summary footer row (Σ munkanap / Σ túlóra / Σ ünnep / Σ hiány)
 *  - CSV export gomb (BOM + UTF-8, Excel hu-HU kompat)
 *  - Employee szűrő Select
 *  - Toggle: hétvége elrejtése + ünnep elrejtése (localStorage-ba mentve)
 *  - aria-label + role="grid"
 */

import { sanitizeCsvValue } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@kgc/ui';
import { useMemo, useState } from 'react';
import type { AttendanceRecord, Employee } from '../api/hr-api';

interface AttendanceMonthlyTableProps {
  year: number;
  month: number;
  employees: Employee[];
  records: AttendanceRecord[];
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function getCellSymbol(record: AttendanceRecord | undefined, isWeekend: boolean): string {
  if (isWeekend) return '–';
  if (!record) return '❌';
  if (record.isHoliday) return '🏖️';
  if (record.actualStart) return '✅';
  return '❌';
}

function formatMinutes(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}h ${m}m`;
}

function downloadCsv(filename: string, rows: string[][]): void {
  // BOM for Excel hu-HU kompat
  const BOM = '\uFEFF';
  const escape = (cell: string) => {
    const safe = sanitizeCsvValue(cell);
    if (/[",\n]/.test(safe)) {
      return `"${safe.replace(/"/g, '""')}"`;
    }
    return safe;
  };
  const csv = BOM + rows.map(r => r.map(escape).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const STORAGE_KEY = 'hr.attendance.table.filters';

interface FilterState {
  hideWeekend: boolean;
  hideHoliday: boolean;
  employeeFilter: string;
}

function loadFilters(): FilterState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { hideWeekend: false, hideHoliday: false, employeeFilter: 'all' };
    const parsed = JSON.parse(raw) as Partial<FilterState>;
    return {
      hideWeekend: parsed.hideWeekend ?? false,
      hideHoliday: parsed.hideHoliday ?? false,
      employeeFilter: parsed.employeeFilter ?? 'all',
    };
  } catch {
    return { hideWeekend: false, hideHoliday: false, employeeFilter: 'all' };
  }
}

function saveFilters(state: FilterState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage disabled / quota — non-fatal
  }
}

export function AttendanceMonthlyTable({
  year,
  month,
  employees,
  records,
}: AttendanceMonthlyTableProps) {
  const { t } = useTranslation();
  const daysInMonth = getDaysInMonth(year, month);
  const dayNumbers = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const initialFilters = useMemo(() => loadFilters(), []);
  const [hideWeekend, setHideWeekend] = useState(initialFilters.hideWeekend);
  const [hideHoliday, setHideHoliday] = useState(initialFilters.hideHoliday);
  const [employeeFilter, setEmployeeFilter] = useState(initialFilters.employeeFilter);
  const [selectedRecord, setSelectedRecord] = useState<{
    record: AttendanceRecord | undefined;
    employee: Employee;
    day: number;
  } | null>(null);

  const updateFilter = (patch: Partial<FilterState>) => {
    const next: FilterState = { hideWeekend, hideHoliday, employeeFilter, ...patch };
    saveFilters(next);
    if (patch.hideWeekend !== undefined) setHideWeekend(patch.hideWeekend);
    if (patch.hideHoliday !== undefined) setHideHoliday(patch.hideHoliday);
    if (patch.employeeFilter !== undefined) setEmployeeFilter(patch.employeeFilter);
  };

  // Employees filter alapján szűrve
  const filteredEmployees = useMemo(() => {
    if (employeeFilter === 'all') return employees;
    return employees.filter(e => e.id === employeeFilter);
  }, [employees, employeeFilter]);

  // Rekordokat indexeljük: employeeProfileId → (day → record)
  const recordMap = useMemo(() => {
    const map = new Map<string, Map<number, AttendanceRecord>>();
    for (const rec of records) {
      const day = new Date(rec.date).getDate();
      if (!map.has(rec.employeeProfileId)) {
        map.set(rec.employeeProfileId, new Map());
      }
      map.get(rec.employeeProfileId)?.set(day, rec);
    }
    return map;
  }, [records]);

  const isWeekend = (day: number): boolean => {
    const d = new Date(year, month - 1, day).getDay();
    return d === 0 || d === 6;
  };

  const visibleDayNumbers = dayNumbers.filter(day => {
    if (hideWeekend && isWeekend(day)) return false;
    return true;
  });

  // Havi summary: összes filtered employee-re
  const monthlySummary = useMemo(() => {
    let totalWorkedDays = 0;
    let totalOvertimeMinutes = 0;
    let totalHolidays = 0;
    let totalMissing = 0;
    for (const emp of filteredEmployees) {
      const empRecs = recordMap.get(emp.id) ?? new Map<number, AttendanceRecord>();
      for (const day of dayNumbers) {
        if (isWeekend(day)) continue;
        const rec = empRecs.get(day);
        if (!rec) {
          totalMissing++;
          continue;
        }
        if (rec.isHoliday) {
          totalHolidays++;
          continue;
        }
        if (rec.actualStart) {
          totalWorkedDays++;
          totalOvertimeMinutes += rec.overtimeMinutes;
        } else {
          totalMissing++;
        }
      }
    }
    return { totalWorkedDays, totalOvertimeMinutes, totalHolidays, totalMissing };
  }, [filteredEmployees, recordMap, dayNumbers, isWeekend]);

  const handleExportCsv = () => {
    const header = [
      'employee_name',
      'employee_email',
      'date',
      'actualStart',
      'actualEnd',
      'breakMinutes',
      'overtimeMinutes',
      'isHoliday',
      'isWeekend',
      'source',
      'note',
    ];
    const rows: string[][] = [header];
    for (const emp of filteredEmployees) {
      const empRecs = recordMap.get(emp.id) ?? new Map<number, AttendanceRecord>();
      for (const day of dayNumbers) {
        const rec = empRecs.get(day);
        if (!rec) continue;
        rows.push([
          emp.userName ?? emp.userId,
          emp.userEmail ?? '',
          rec.date,
          rec.actualStart ?? '',
          rec.actualEnd ?? '',
          String(rec.breakMinutes),
          String(rec.overtimeMinutes),
          rec.isHoliday ? 'true' : 'false',
          rec.isWeekend ? 'true' : 'false',
          rec.source,
          rec.note ?? '',
        ]);
      }
    }
    downloadCsv(`jelenlet-${year}-${String(month).padStart(2, '0')}.csv`, rows);
  };

  return (
    <div className="space-y-3">
      {/* Filter + export bar */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={employeeFilter} onValueChange={v => updateFilter({ employeeFilter: v })}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder={t('hr.attendance.filterEmployee')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('hr.attendance.allEmployees')}</SelectItem>
            {employees.map(emp => (
              <SelectItem key={emp.id} value={emp.id}>
                {emp.userName ?? emp.userId}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={hideWeekend}
            onChange={e => updateFilter({ hideWeekend: e.target.checked })}
            className="rounded"
          />
          {t('hr.attendance.hideWeekend')}
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={hideHoliday}
            onChange={e => updateFilter({ hideHoliday: e.target.checked })}
            className="rounded"
          />
          {t('hr.attendance.hideHoliday')}
        </label>

        <Button variant="outline" size="sm" onClick={handleExportCsv}>
          {t('hr.attendance.exportCsv')}
        </Button>
      </div>

      {/* Grid table */}
      <div className="overflow-x-auto" role="grid" aria-label={t('hr.attendance.title')}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 bg-background z-10 min-w-[150px]">
                {t('hr.employee.name')}
              </TableHead>
              {visibleDayNumbers.map(day => (
                <TableHead
                  key={day}
                  className={`text-center min-w-[36px] px-1 text-xs ${
                    isWeekend(day) ? 'text-muted-foreground' : ''
                  }`}
                >
                  {day}
                </TableHead>
              ))}
              <TableHead className="text-center">{t('hr.attendance.workedDays')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEmployees.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={visibleDayNumbers.length + 2}
                  className="text-center text-muted-foreground py-8"
                >
                  {t('hr.employee.noEmployees')}
                </TableCell>
              </TableRow>
            )}
            {filteredEmployees.map(employee => {
              const empRecords = recordMap.get(employee.id) ?? new Map<number, AttendanceRecord>();
              const workedDays = Array.from(empRecords.values()).filter(
                r => r.actualStart && !r.isHoliday
              ).length;

              return (
                <TableRow key={employee.id}>
                  <TableCell className="sticky left-0 bg-background z-10 font-medium">
                    {employee.userName ?? employee.userId}
                  </TableCell>
                  {visibleDayNumbers.map(day => {
                    const record = empRecords.get(day);
                    const weekend = isWeekend(day);
                    const hideThis = hideHoliday && record?.isHoliday === true;
                    const ariaLabel = `${employee.userName ?? employee.userId} ${year}-${month}-${day} ${
                      record?.actualStart
                        ? `${record.actualStart}-${record.actualEnd ?? '?'}`
                        : 'hiány'
                    }`;
                    return (
                      <TableCell
                        key={day}
                        role="gridcell"
                        aria-label={ariaLabel}
                        className={`text-center px-1 text-sm cursor-pointer hover:bg-muted/50 ${
                          weekend ? 'bg-muted/30' : ''
                        } ${hideThis ? 'opacity-30' : ''}`}
                        onClick={() => setSelectedRecord({ record, employee, day })}
                      >
                        {getCellSymbol(record, weekend)}
                      </TableCell>
                    );
                  })}
                  <TableCell className="text-center font-medium">{workedDays}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell className="sticky left-0 bg-background z-10 font-semibold">
                {t('hr.attendance.summaryLabel')}
              </TableCell>
              <TableCell
                colSpan={visibleDayNumbers.length}
                className="text-xs text-muted-foreground"
              >
                {t('hr.attendance.summaryWorkedDays', { count: monthlySummary.totalWorkedDays })} ·{' '}
                {t('hr.attendance.summaryOvertime', {
                  duration: formatMinutes(monthlySummary.totalOvertimeMinutes),
                })}{' '}
                · {t('hr.attendance.summaryHolidays', { count: monthlySummary.totalHolidays })} ·{' '}
                {t('hr.attendance.summaryMissing', { count: monthlySummary.totalMissing })}
              </TableCell>
              <TableCell className="text-center font-semibold">
                {monthlySummary.totalWorkedDays}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>

      {/* Cell detail Sheet */}
      <Sheet open={selectedRecord !== null} onOpenChange={open => !open && setSelectedRecord(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md">
          {selectedRecord && (
            <>
              <SheetHeader>
                <SheetTitle>
                  {selectedRecord.employee.userName ?? selectedRecord.employee.userId}
                </SheetTitle>
                <SheetDescription>
                  {year}-{String(month).padStart(2, '0')}-
                  {String(selectedRecord.day).padStart(2, '0')}
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6 space-y-3 text-sm">
                {selectedRecord.record === undefined ? (
                  <p className="text-muted-foreground">{t('hr.attendance.noRecord')}</p>
                ) : (
                  <>
                    <div>
                      <span className="text-xs text-muted-foreground">
                        {t('hr.attendance.startedAt')}
                      </span>
                      <p className="font-medium">{selectedRecord.record.actualStart ?? '–'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">
                        {t('hr.attendance.endedAt')}
                      </span>
                      <p className="font-medium">{selectedRecord.record.actualEnd ?? '–'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">
                        {t('hr.attendance.breakMinutesLabel')}
                      </span>
                      <p className="font-medium">{selectedRecord.record.breakMinutes} min</p>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">
                        {t('hr.attendance.overtimeLabel')}
                      </span>
                      <p className="font-medium">
                        {formatMinutes(selectedRecord.record.overtimeMinutes)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {selectedRecord.record.isHoliday && (
                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs dark:bg-blue-900/30">
                          🏖️ {t('hr.attendance.holidayLabel')}
                        </span>
                      )}
                      {selectedRecord.record.isWeekend && (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                          {t('hr.attendance.weekendLabel')}
                        </span>
                      )}
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">
                        {t('hr.attendance.sourceLabel')}
                      </span>
                      <p className="font-medium">{selectedRecord.record.source}</p>
                    </div>
                    {selectedRecord.record.note && (
                      <div>
                        <span className="text-xs text-muted-foreground">
                          {t('hr.attendance.noteLabel')}
                        </span>
                        <p className="font-medium">{selectedRecord.record.note}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
